import { PRODUCT_VIEW_LAYER } from '../../../../AssetTargetsDialog/helpers/assetProductLinks';
import { groupProductLinks } from '../../ProductLinksSections/helpers/groupProductLinks';

const getListingProductKey = (product) => (
  `${product._id}:${product.isDraft === true ? 'draft' : 'live'}`
);

const getProductLinkTitle = (product) => product.title || product._id;

const getVariantLinkTitle = (variant) => variant.title || variant.variantId;

const buildProductChip = (productLink, { isDraft, isMaster }) => {
  const title = getProductLinkTitle(productLink);

  return {
    kind: 'product',
    title,
    tooltipText: title,
    isDraft,
    isMaster,
    link: productLink,
  };
};

const buildVariantChip = (variantLink, { isDraft, isMaster }) => {
  const title = getVariantLinkTitle(variantLink);

  return {
    kind: 'variant',
    title,
    tooltipText: title,
    isDraft,
    isMaster,
    link: variantLink,
  };
};

const resolveListingProductTitle = (listingId, {
  listingLink,
  masterProductId,
  masterProducts = [],
}) => {
  if (listingLink?.title) return listingLink.title;

  const masterCandidates = masterProductId
    ? masterProducts.filter(({ _id }) => String(_id) === String(masterProductId))
    : masterProducts;

  for (const master of masterCandidates) {
    const channelEntry = (master.channels || []).find(
      (channel) => String(channel?.listingProduct?._id) === String(listingId),
    );

    if (channelEntry?.listingProduct?.title) {
      return channelEntry.listingProduct.title;
    }
  }

  return '';
};

const buildMasterRows = (grouped) => {
  const masterRows = [];
  const masterProductIds = new Set(grouped.master.products.map(({ _id }) => String(_id)));

  grouped.master.products.forEach((product) => {
    masterRows.push({
      key: `master-product:${product._id}`,
      isNested: false,
      viewContext: { layer: PRODUCT_VIEW_LAYER.MASTER },
      productId: product._id,
      linkVariantId: null,
      isVariant: false,
      productTitle: getProductLinkTitle(product),
      chips: [buildProductChip(product, {
        isDraft: false,
        isMaster: true,
      })],
    });

    grouped.master.variants
      .filter((variant) => String(variant.productId) === String(product._id))
      .forEach((variant) => {
        masterRows.push({
          key: `master-variant:${variant.variantId}`,
          isNested: true,
          viewContext: { layer: PRODUCT_VIEW_LAYER.MASTER },
          productId: product._id,
          linkVariantId: variant.variantId,
          isVariant: true,
          productTitle: getProductLinkTitle(product),
          chips: [buildVariantChip(variant, {
            isDraft: false,
            isMaster: true,
          })],
        });
      });
  });

  grouped.master.variants
    .filter((variant) => !masterProductIds.has(String(variant.productId)))
    .forEach((variant) => {
      masterRows.push({
        key: `master-variant:${variant.variantId}`,
        isNested: false,
        viewContext: { layer: PRODUCT_VIEW_LAYER.MASTER },
        productId: variant.productId,
        linkVariantId: variant.variantId,
        isVariant: true,
        productTitle: getVariantLinkTitle(variant),
        chips: [buildVariantChip(variant, {
          isDraft: false,
          isMaster: true,
        })],
      });
    });

  return masterRows;
};

const collectChannelListingRows = (channel, { masterProducts }) => {
  const listingsMap = new Map();

  channel.products.forEach((listing) => {
    listingsMap.set(getListingProductKey(listing), {
      listing,
      hasProductGalleryUsage: true,
    });
  });

  channel.variants.forEach((variant) => {
    const key = `${variant.productId}:${variant.isDraft === true ? 'draft' : 'live'}`;
    if (!listingsMap.has(key)) {
      listingsMap.set(key, {
        listing: {
          _id: variant.productId,
          title: resolveListingProductTitle(variant.productId, {
            masterProductId: variant.masterProductId,
            masterProducts,
          }),
          isDraft: variant.isDraft === true,
          type: 'listing',
          salesChannel: variant.salesChannel,
          masterProductId: variant.masterProductId,
        },
        hasProductGalleryUsage: false,
      });
    }
  });

  return [...listingsMap.entries()].map(([listingKey, { listing, hasProductGalleryUsage }]) => {
    const listingVariantLinks = channel.variants.filter(
      (variant) => `${variant.productId}:${variant.isDraft === true ? 'draft' : 'live'}` === listingKey,
    );
    const listingLink = channel.products.find(
      (product) => getListingProductKey(product) === listingKey,
    );
    const listingTitle = resolveListingProductTitle(listing._id, {
      listingLink,
      masterProductId: listing.masterProductId,
      masterProducts,
    }) || listing.title || listing._id;
    const isDraft = listing.isDraft === true;
    const chips = [];

    if (hasProductGalleryUsage) {
      chips.push(buildProductChip(listingLink || listing, {
        isDraft,
        isMaster: false,
      }));
    }

    listingVariantLinks.forEach((variant) => {
      chips.push(buildVariantChip(variant, {
        isDraft: variant.isDraft === true,
        isMaster: false,
      }));
    });

    return {
      key: `channel:${channel.channelId}:listing:${listingKey}`,
      viewContext: {
        layer: isDraft ? PRODUCT_VIEW_LAYER.STAGING : PRODUCT_VIEW_LAYER.LIVE,
        listingProductId: listing._id,
      },
      productId: listing._id,
      linkVariantId: null,
      isVariant: false,
      productTitle: listingTitle,
      chips,
    };
  });
};

export default function buildAltTextUsageRows({
  products = [],
  productVariants = [],
  masterProducts = [],
  salesChannels = [],
}) {
  const grouped = groupProductLinks({
    products, productVariants, masterProducts, salesChannels,
  });
  const masterRows = buildMasterRows(grouped);
  const channelSections = grouped.channels
    .map((channel) => ({
      channelId: channel.channelId,
      rows: collectChannelListingRows(channel, { masterProducts }),
    }))
    .filter((section) => section.rows.length);

  return {
    masterRows,
    channelSections,
    hasUsageRows: masterRows.length > 0 || channelSections.length > 0,
  };
}
